import {
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
} from '@/shared/components/drawer';
import { Button, Avatar } from '@nextui-org/react';
import { X } from 'lucide-react';
import type {
	IntelligenceDrawerProps,
	IntelligenceDrawerType,
} from '../../types/intelligence-drawer.types';
import type { WebReviewSource, ReviewSourceTypes } from '@/modules/agent/types';
import InlineSources from '@/modules/agent/components/review-responses/InlineSources';
import ZAvatarGradient from '@/shared/components/avatarGradient';
import { VENDOR_PREDEFINED_COMPLIANCE_CERTS } from '@/modules/agent/constants/vendorAssessmentConstants';
import { useState } from 'react';

export const IntelligenceDrawer = ({
	isOpen,
	onClose,
	drawerType,
	data,
}: IntelligenceDrawerProps) => {
	// State for managing source preview (not used but required by InlineSources)
	const [selectedSource, setSelectedSource] =
		useState<ReviewSourceTypes | undefined>();

	// Convert web source URLs to ReviewSourceTypes format for InlineSources component
	const convertUrlsToSources = (urls: string[]): WebReviewSource[] => {
		return urls.map((url, index) => {
			try {
				const hostname = new URL(url).hostname.replace(/^www\./, '');
				return {
					source_id: `web-${index}`,
					source_number: index + 1,
					is_web_source: true,
					web_source_url: url,
					source_file_name: hostname,
					file_type: 'web',
					sheet_name: null,
					is_image: null,
					record_index: null,
				};
			} catch {
				return {
					source_id: `web-${index}`,
					source_number: index + 1,
					is_web_source: true,
					web_source_url: url,
					source_file_name: url,
					file_type: 'web',
					sheet_name: null,
					is_image: null,
					record_index: null,
				};
			}
		});
	};

	// Source click handler for InlineSources
	const onSourceClick = (source: ReviewSourceTypes) => {
		const webSource = source as WebReviewSource;
		if (webSource.is_web_source && webSource.web_source_url) {
			window.open(webSource.web_source_url, '_blank');
		}
	};

	// Required by InlineSources but not used
	const setListOfSourcesForSelectedSources = () => {
		// No-op
	};

	// Get drawer title based on type
	const getTitle = (): string => {
		switch (drawerType) {
			case 'breachHistory':
				return 'Breach History';
			case 'darkWebChatter':
				return 'Dark Web Chatter';
			case 'compliance':
				return 'Compliance';
			case 'news':
				return 'News';
			default:
				return '';
		}
	};

	// Get drawer subtitle based on type
	const getSubtitle = (): string => {
		switch (drawerType) {
			case 'breachHistory':
			case 'darkWebChatter':
				return 'Compiled from various sources on the internet';
			case 'compliance':
			case 'news':
				return '';
			default:
				return '';
		}
	};

	// Render content based on drawer type
	const renderContent = () => {
		if (!drawerType) return null;

		switch (drawerType) {
			case 'breachHistory': {
				const items = data.breachHistory;

				if (items.length === 0) {
					return (
						<div className="flex items-center justify-center h-full">
							<p className="text-sm text-[#A1A1AA]">No breach history found</p>
						</div>
					);
				}

				return (
					<div className="flex flex-col gap-6 py-0">
						{items.map((event, index) => (
							<div
								key={index}
								className="border-b border-[#E4E4E7] pb-6 last:border-b-0"
							>
								<p className="text-sm font-medium mb-1">{event.title}</p>
								<p className="text-sm text-[#777780]">{event.text}</p>
								<div className="relative h-6 mt-3">
									<InlineSources
										sources={convertUrlsToSources(event.web_source_urls)}
										onSourceClick={onSourceClick}
										setListOfSourcesForSelectedSources={
											setListOfSourcesForSelectedSources
										}
										tooltipPlacement="bottom"
										position="relative bottom-0"
									/>
								</div>
							</div>
						))}
					</div>
				);
			}

			case 'darkWebChatter': {
				const items = data.darkWebChatter;

				if (items.length === 0) {
					return (
						<div className="flex items-center justify-center h-full">
							<p className="text-sm text-[#A1A1AA]">
								No dark web chatter found
							</p>
						</div>
					);
				}

				return (
					<div className="flex flex-col gap-6 py-0">
						{items.map((finding, index) => (
							<div
								key={index}
								className="border-b border-[#E4E4E7] pb-6 last:border-b-0"
							>
								<p className="text-sm font-medium mb-2">{finding.title}</p>
								<p className="text-sm text-[#777780]">{finding.text}</p>
								<div className="relative h-6 mt-3">
									<InlineSources
										sources={convertUrlsToSources(finding.web_source_urls)}
										onSourceClick={onSourceClick}
										setListOfSourcesForSelectedSources={
											setListOfSourcesForSelectedSources
										}
										tooltipPlacement="bottom"
										position="relative bottom-0"
									/>
								</div>
							</div>
						))}
					</div>
				);
			}

			case 'compliance': {
				const items = data.compliance;

				if (items.length === 0) {
					return (
						<div className="flex items-center justify-center h-full">
							<p className="text-sm text-[#A1A1AA]">
								No compliance certifications found
							</p>
						</div>
					);
				}

				return (
					<div className="py-0">
						<div>
							{items.map((cert, index) => (
								<div
									key={index}
									className="border-b border-[#E4E4E7] py-4 flex items-center gap-4"
								>
									{/* Certification Icon - using logos when available */}
									{(() => {
										// Look for matching logo in predefined certs
										const img = VENDOR_PREDEFINED_COMPLIANCE_CERTS.find(
											(predefinedCert) =>
												predefinedCert.label.toLowerCase() ===
												cert.name.toLowerCase(),
										)?.logo;

										return img ? (
											<Avatar
												size="lg"
												name={cert.name}
												src={img}
												className="max-w-10 max-h-10"
											/>
										) : (
											// Fallback to using ZAvatarGradient if available
											typeof ZAvatarGradient !== 'undefined' ? (
												<ZAvatarGradient
													name={cert.name}
													textSize={14}
													size={40}
													backgroundColor="#E4E4E7"
													shadow="shadow-none"
													textColor="text-[#171717]"
												/>
											) : (
												// Or default to simple circular div with initials
												<div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium overflow-hidden">
													{cert.name.slice(0, 3)}
												</div>
											)
										);
									})()}

									<div className="flex-grow">
										<p className="text-sm font-medium text-[#171717]">
											{cert.name}
										</p>
									</div>

									{/* Source indicators */}
									<div className="flex items-center gap-1">
										<InlineSources
											sources={convertUrlsToSources(cert.web_source_urls)}
											onSourceClick={onSourceClick}
											setListOfSourcesForSelectedSources={
												setListOfSourcesForSelectedSources
											}
											tooltipPlacement="left"
											position="relative bottom-0"
										/>
									</div>
								</div>
							))}
						</div>
					</div>
				);
			}

			case 'news': {
				const items = data.news;

				if (items.length === 0) {
					return (
						<div className="flex items-center justify-center h-full">
							<p className="text-sm text-[#A1A1AA]">No news articles found</p>
						</div>
					);
				}

				return (
					<div className="flex flex-col gap-6 py-0">
						{items.map((article, index) => (
							<div
								key={index}
								className="border-b border-[#E4E4E7] pb-6 last:border-b-0"
							>
								<p className="text-sm font-medium mb-1">{article.title}</p>
								<p className="text-sm text-[#777780]">{article.text}</p>
								<div className="relative h-6 mt-3">
									<InlineSources
										sources={convertUrlsToSources(article.web_source_urls)}
										onSourceClick={onSourceClick}
										setListOfSourcesForSelectedSources={
											setListOfSourcesForSelectedSources
										}
										tooltipPlacement="bottom"
										position="relative bottom-0"
									/>
								</div>
							</div>
						))}
					</div>
				);
			}

			default:
				return null;
		}
	};

	return (
		<Drawer
			isOpen={isOpen}
			onClose={onClose}
			placement={'right' as any}
			hideCloseButton
		>
			<DrawerContent className="h-full flex flex-col rounded-r-none border-l border-[#E4E4E7]">
				<DrawerHeader className="border-b border-[#E4E4E7] py-4 px-6 flex flex-col">
					<div className="flex justify-between items-start w-full">
						<h2 className="text-lg font-medium">{getTitle()}</h2>
						<Button
							isIconOnly
							variant="light"
							onPress={onClose}
							className="w-4 h-4 text-[#777780] data-[hover=true]:bg-transparent data-[focus=true]:bg-transparent data-[focus-visible=true]:outline-none"
						>
							<X size={16} />
						</Button>
					</div>
					{getSubtitle() && (
						<p className="text-sm font-light text-[#777780]">{getSubtitle()}</p>
					)}
				</DrawerHeader>

				<DrawerBody className="overflow-y-auto p-6 flex-grow">
					{renderContent()}
				</DrawerBody>
			</DrawerContent>
		</Drawer>
	);
};
